import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';

const SaveViewButton = ({
  hasChanges,
  savedViews,
  currentView,
  onSaveView,
  onLoadView,
  onDeleteView
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [viewName, setViewName] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSaveClick = () => {
    if (hasChanges) {
      setShowSaveModal(true);
      setIsOpen(false);
    } else {
      setIsOpen(!isOpen);
    }
  };

  const handleSaveView = () => {
    if (viewName.trim()) {
      onSaveView(viewName.trim());
      setViewName('');
      setShowSaveModal(false);
    }
  };

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      <button
        onClick={handleSaveClick}
        className={`btn ${hasChanges ? 'btn-setlist-primary' : 'btn-setlist-secondary'}`}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M5.83333 2.5V5.33333C5.83333 5.80004 5.83333 6.0334 5.92416 6.21166C6.00406 6.36846 6.13154 6.49594 6.28834 6.57584C6.4666 6.66667 6.69996 6.66667 7.16667 6.66667H12.8333C13.3 6.66667 13.5334 6.66667 13.7117 6.57584C13.8685 6.49594 13.9959 6.36846 14.0758 6.21166C14.1667 6.0334 14.1667 5.80004 14.1667 5.33333V3.33333M14.1667 17.5V12.1667C14.1667 11.7 14.1667 11.4666 14.0758 11.2883C13.9959 11.1315 13.8685 11.0041 13.7117 10.9242C13.5334 10.8333 13.3 10.8333 12.8333 10.8333H7.16667C6.69996 10.8333 6.4666 10.8333 6.28834 10.9242C6.13154 11.0041 6.00406 11.1315 5.92416 11.2883C5.83333 11.4666 5.83333 11.7 5.83333 12.1667V17.5M17.5 7.77124V13.5C17.5 14.9001 17.5 15.6002 17.2275 16.135C16.9878 16.6054 16.6054 16.9878 16.135 17.2275C15.6002 17.5 14.9001 17.5 13.5 17.5H6.5C5.09987 17.5 4.3998 17.5 3.86502 17.2275C3.39462 16.9878 3.01217 16.6054 2.77248 16.135C2.5 15.6002 2.5 14.9001 2.5 13.5V6.5C2.5 5.09987 2.5 4.3998 2.77248 3.86502C3.01217 3.39462 3.39462 3.01217 3.86502 2.77248C4.3998 2.5 5.09987 2.5 6.5 2.5H12.2288C12.6364 2.5 12.8402 2.5 13.0321 2.54605C13.2021 2.58688 13.3647 2.65422 13.5138 2.7456C13.6819 2.84867 13.8261 2.9928 14.1144 3.28105L16.719 5.88562C17.0072 6.17387 17.1513 6.318 17.2544 6.48619C17.3458 6.63531 17.4131 6.79789 17.4539 6.96795C17.5 7.15976 17.5 7.36358 17.5 7.77124Z"
            stroke={hasChanges ? '#FFFFFF' : '#414651'}
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>{hasChanges ? 'Save View' : 'Saved Views'}</span>
      </button>

      {isOpen && !hasChanges && (
        <div
          className="card shadow-lg border-0"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '8px',
            width: '280px',
            zIndex: 1050
          }}
        >
          <div className="card-body p-0">
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
              <h6 className="mb-0 font-weight-bold">Saved Views</h6>
              <button
                className="btn btn-link text-muted p-0"
                onClick={() => setIsOpen(false)}
                style={{ fontSize: '20px' }}
              >
                <i className="fal fa-times"></i>
              </button>
            </div>

            {savedViews && savedViews.length > 0 ? (
              <ul className="list-group list-group-flush">
                {savedViews.map((view, index) => (
                  <li
                    key={index}
                    className="list-group-item d-flex justify-content-between align-items-center"
                  >
                    <button
                      className="btn btn-link p-0 text-left flex-grow-1"
                      onClick={() => {
                        onLoadView(view);
                        setIsOpen(false);
                      }}
                    >
                      {view.name}
                      {currentView === view.name && (
                        <i className="fal fa-check text-success ml-2"></i>
                      )}
                    </button>
                    <button
                      className="btn btn-link text-danger p-0 ml-2"
                      onClick={() => onDeleteView(view)}
                    >
                      <i className="fal fa-trash"></i>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-4 text-center text-muted">
                <i className="fal fa-inbox fa-2x mb-2"></i>
                <p className="mb-0 small">No saved views yet</p>
              </div>
            )}

            <div className="p-3 border-top">
              <button
                className="btn btn-sm btn-primary btn-block"
                onClick={() => {
                  setShowSaveModal(true);
                  setIsOpen(false);
                }}
              >
                <i className="fal fa-plus mr-2"></i>
                Create New View
              </button>
            </div>
          </div>
        </div>
      )}

      {showSaveModal && (
        <div
          className="modal d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowSaveModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Save Current View</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowSaveModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>View Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter view name..."
                    value={viewName}
                    onChange={(e) => setViewName(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveView();
                      }
                    }}
                    autoFocus
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setViewName('');
                    setShowSaveModal(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveView}
                  disabled={!viewName.trim()}
                >
                  Save View
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

SaveViewButton.propTypes = {
  hasChanges: PropTypes.bool.isRequired,
  savedViews: PropTypes.array,
  currentView: PropTypes.string,
  onSaveView: PropTypes.func.isRequired,
  onLoadView: PropTypes.func.isRequired,
  onDeleteView: PropTypes.func.isRequired
};

SaveViewButton.defaultProps = {
  savedViews: [],
  currentView: null
};

export default SaveViewButton;
